/**
 * createPaymentIntentWithAuthRetry.js
 *
 * Calls the `create-payment-intent` Supabase Edge Function via a direct
 * `fetch` request (rather than `supabase.functions.invoke`) so that the
 * actual error body (code, message) is always readable — `invoke` swallows
 * non-2xx bodies and only surfaces a generic "non-2xx status code" string.
 *
 * Retry strategy:
 *   1. First attempt with the current session token.
 *   2. If a 401 is returned, silently refresh the session and retry once.
 *   3. Any other error is surfaced immediately without retrying.
 *
 * @module createPaymentIntentWithAuthRetry
 */

/**
 * Initialises a Stripe PaymentIntent via the Edge Function, with one
 * automatic auth-refresh retry on 401 responses.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {{
 *   packId: string,
 *   packName: string,
 *   amount: number,
 *   currency: string,
 *   credits?: number,
 *   customerInfo?: object
 * }} params - Payment package details
 * @param {{ logger?: (event: string, payload?: object) => void }} [options]
 * @returns {Promise<{
 *   ok: boolean,
 *   data?: { clientSecret: string, paymentIntentId: string, [key: string]: any },
 *   code?: string,
 *   message?: string
 * }>}
 */
export async function createPaymentIntentWithAuthRetry(supabase, params, options = {}) {
  // Use the provided logger or a no-op so call sites don't need to guard
  const log = options?.logger ?? (() => {});

  log('payment_init_start', { packId: params?.packId, amount: params?.amount });

  // Guard — ensure the Supabase auth client is available before proceeding
  if (!supabase?.auth) {
    log('auth_client_not_hydrated');
    return {
      ok: false,
      code: 'NOT_AUTHENTICATED',
      message: 'Authentication client is not ready. Please refresh the page and try again.',
    };
  }

  // Retrieve the current session token
  const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
  let token = sessionData?.session?.access_token;

  if (sessionError || !token) {
    log('auth_session_missing', { sessionError: sessionError?.message });
    return {
      ok: false,
      code: 'NOT_AUTHENTICATED',
      message: 'User not authenticated. Please sign in again.',
    };
  }

  log('auth_token_retrieved', { hasToken: true });

  // Read Supabase config from env — required for the direct fetch call
  const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
  const ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

  if (!SUPABASE_URL || !ANON_KEY) {
    log('env_missing', { hasUrl: !!SUPABASE_URL, hasAnonKey: !!ANON_KEY });
    return {
      ok: false,
      code: 'CONFIG_ERROR',
      message: 'Payment service is not configured. Please contact support.',
    };
  }

  // ─── Internal helper: call the Edge Function with a given access token ──────

  /**
   * Performs a single direct fetch to the create-payment-intent Edge Function.
   * Always reads the response body so we get the actual error code/message.
   *
   * @param {string} accessToken - JWT access token for the Authorization header
   * @returns {Promise<{ data: object|null, error: object|null }>}
   */
  const callEdgeFunction = async (accessToken) => {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          paymentData: {
            packageId: params?.packId,
            amount: params?.amount,
            credits: params?.credits,
            ...(typeof params?.discountAmount === 'number' && params.discountAmount > 0
              ? { discountAmount: params.discountAmount }
              : {}),
            ...(params?.promotionCodeId ? { promotionCodeId: params.promotionCodeId } : {}),
          },
          customerInfo: params?.customerInfo ?? {},
        }),
      });

      // Always parse the body — even on error — to surface the Edge Function message
      let json = null;
      try {
        json = await res.json();
      } catch {
        // Non-JSON response (e.g. empty 204 or HTML error page)
        json = { error: `HTTP ${res.status} — non-JSON response`, code: 'NON_JSON_RESPONSE' };
      }

      log('edge_fn_response', { status: res.status, ok: res.ok, code: json?.code, error: json?.error });

      if (!res.ok) {
        return {
          data: null,
          error: {
            message: json?.error || `HTTP ${res.status}`,
            code: json?.code || 'HTTP_ERROR',
            status: res.status,
            raw: json,
          },
        };
      }

      return { data: json, error: null };
    } catch (fetchErr) {
      log('edge_fn_fetch_exception', { message: fetchErr?.message });
      return {
        data: null,
        error: { message: fetchErr?.message || 'Network error', code: 'FETCH_EXCEPTION', status: 0 },
      };
    }
  };

  // ─── Helper: detect 401 / auth errors ────────────────────────────────────

  /**
   * Returns true if the error object indicates an authentication failure.
   * Checks HTTP status, message text, and common JWT error keywords.
   *
   * @param {{ status?: number, message?: string }|null} error
   * @returns {boolean}
   */
  const is401 = (error) => {
    const msg = (error?.message || '').toLowerCase();
    return (
      error?.status === 401 ||
      msg.includes('401') ||
      msg.includes('unauthorized') ||
      msg.includes('jwt') ||
      msg.includes('invalid token')
    );
  };

  // ─── First attempt ────────────────────────────────────────────────────────

  log('payment_intent_request', { attempt: 1, method: 'fetch' });
  const { data: firstData, error: firstError } = await callEdgeFunction(token);

  if (!firstError && firstData?.clientSecret) {
    log('payment_intent_success', { attempt: 1, paymentIntentId: firstData?.paymentIntentId });
    return { ok: true, data: firstData };
  }

  // Non-auth error — surface the actual Edge Function error code immediately
  if (!is401(firstError)) {
    const msg = firstError?.message || firstData?.error || '';
    const code = firstError?.code || firstData?.code || 'PAYMENT_INIT_FAILED';
    log('payment_intent_error', { attempt: 1, status: firstError?.status, code, message: msg, raw: firstError?.raw });
    return {
      ok: false,
      code: 'PAYMENT_INIT_FAILED',
      message: msg || 'Failed to initialize payment. Please try again.',
      _edgeCode: code,
    };
  }

  // ─── 401 detected: refresh session and retry once ─────────────────────────

  log('auth_refresh_attempt', { reason: '401_on_first_attempt' });

  const { data: refreshData, error: refreshError } = await supabase?.auth?.refreshSession();
  const newToken = refreshData?.session?.access_token;

  if (refreshError || !newToken) {
    log('auth_refresh_failed', { refreshError: refreshError?.message });
    return {
      ok: false,
      code: 'AUTH_RETRY_FAILED',
      message: 'Your session could not be refreshed. Please sign in again.',
    };
  }

  log('auth_refresh_success', { hasNewToken: true });

  // ─── Second attempt with the refreshed token ──────────────────────────────

  log('payment_intent_request', { attempt: 2, method: 'fetch_refreshed' });
  const { data: retryData, error: retryError } = await callEdgeFunction(newToken);

  if (!retryError && retryData?.clientSecret) {
    log('payment_intent_success', { attempt: 2, paymentIntentId: retryData?.paymentIntentId });
    return { ok: true, data: retryData };
  }

  const retryMsg = retryError?.message || retryData?.error || '';
  const retryCode = retryError?.code || retryData?.code || 'PAYMENT_INIT_FAILED';
  log('payment_intent_error', { attempt: 2, status: retryError?.status, code: retryCode, message: retryMsg, raw: retryError?.raw });

  // If still a 401 after refresh, the session is truly invalid
  if (is401(retryError)) {
    return {
      ok: false,
      code: 'AUTH_RETRY_FAILED',
      message: 'Authentication failed after retry. Please sign in again.',
    };
  }

  return {
    ok: false,
    code: 'PAYMENT_INIT_FAILED',
    message: retryMsg || 'Failed to initialize payment. Please try again.',
    _edgeCode: retryCode,
  };
}
